import { epochSecondsToDate, z } from '@customerio/hydra-validation';
import type {
  GeosetsFenceModel,
  GeosetsFencePointModel,
  GeosetsGeosetModel,
  GeosetsGeosetUsageCampaign,
  GeosetsGeosetUsageModel,
  GeosetsGeosetUsageSegment,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * Geoset entity — workspace-scoped named collections of geofences that
 * campaigns and segments target ("a single geofence is a set of one").
 *
 * The wire id is numeric, but this data layer normalizes ids to strings
 * so routes, query keys, and future FK-following helpers all share the
 * same id shape — matching the geofence entity it sits beside.
 */

/* ------------------------------------------------------------------------- */
/* Type                                                                       */
/* ------------------------------------------------------------------------- */

export type Geoset = Omit<GeosetsGeosetModel, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

/* ------------------------------------------------------------------------- */
/* Schemas — only the transforms                                              */
/* ------------------------------------------------------------------------- */

const GeosetFromApi = readSchema<Geoset>(
  z.looseObject({
    id: z.number().transform(String),
    created_at: epochSecondsToDate,
    updated_at: epochSecondsToDate,
  }),
);

/* ------------------------------------------------------------------------- */
/* List params                                                                */
/* ------------------------------------------------------------------------- */

/**
 * The list endpoint (`GET …/geosets`) takes no query params today — it
 * returns every geoset in the workspace in one unpaginated response. The
 * type is kept as an (empty) params object so the shared key factory and a
 * future filtered list share the same shape as the other entities.
 */
export type GeosetListParams = Record<string, never>;

/* ------------------------------------------------------------------------- */
/* Entity                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export const geoset = defineEntity<Geoset, never, GeosetListParams, undefined, 'workspace'>({
  name: 'geoset',
  scope: 'workspace',
  pk: (g) => g.id,
  schema: { read: GeosetFromApi },
});

export const geosetKeys = geoset.keys;

export function parseGeoset(raw: GeosetsGeosetModel): Geoset {
  return geoset.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Geoset fence — the membership read model                                   */
/* ------------------------------------------------------------------------- */

/**
 * A fence as returned by `geosets.getFences`. Carries the same data as a
 * workspace `Geofence`, with the free-form `attributes` standing in for the
 * geofence's `metadata` (address / saved radius unit). The geoset detail page
 * adapts these directly (see `geosetFenceToGeofence`) — no enrichment read
 * against the `/geofences` list endpoint is needed.
 */
export type GeosetFence = Omit<GeosetsFenceModel, 'id' | 'created_at' | 'updated_at'> & {
  id: string;
  created_at: Date;
  updated_at: Date;
};

const GeosetFenceFromApi = readSchema<GeosetFence>(
  z.looseObject({
    id: z.number().transform(String),
    created_at: epochSecondsToDate,
    updated_at: epochSecondsToDate,
  }),
);

export function parseGeosetFence(raw: GeosetsFenceModel): GeosetFence {
  return GeosetFenceFromApi.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Geoset fences list params — the server-driven table read                   */
/* ------------------------------------------------------------------------- */

/** Sort fields the `getFences` endpoint accepts (`external_id` is not sortable server-side). */
export type GeosetFencesSortField = 'id' | 'name' | 'radius' | 'created_at';

export type GeosetFencesSortOrder = 'asc' | 'desc';

/**
 * Query params for a single `getFences` page. Part of the query key, so two
 * reads with the same params share one cache slot. `search` matches name +
 * external_id (case-insensitive substring); `bbox` is
 * `min_lng,min_lat,max_lng,max_lat` and matches fences whose circle intersects
 * the box (min_lng > max_lng spans the antimeridian).
 */
export interface GeosetFencesListParams {
  page: number;
  pageSize: number;
  sort: GeosetFencesSortField;
  order: GeosetFencesSortOrder;
  search?: string;
  bbox?: string;
}

/* ------------------------------------------------------------------------- */
/* Geoset fence point — the slim map read model                               */
/* ------------------------------------------------------------------------- */

/**
 * A fence as returned by `geosets.getFencePoints` — the compact circle or
 * polygon footprint the overview map needs, with the id normalized to a
 * string like every other entity here. The full-set points payload replaces
 * downloading every full fence model for the map.
 */
export type GeosetFencePoint = Omit<GeosetsFencePointModel, 'id'> & {
  id: string;
};

const GeosetFencePointFromApi = readSchema<GeosetFencePoint>(
  z.looseObject({
    id: z.number().transform(String),
  }),
);

export function parseGeosetFencePoint(raw: GeosetsFencePointModel): GeosetFencePoint {
  return GeosetFencePointFromApi.parse(raw);
}

/* ------------------------------------------------------------------------- */
/* Geoset usage — what targets a geoset                                       */
/* ------------------------------------------------------------------------- */

export type GeosetUsageCampaign = GeosetsGeosetUsageCampaign;

export type GeosetUsageSegment = GeosetsGeosetUsageSegment;

export type GeosetUsage = GeosetsGeosetUsageModel;
